/**
 * Returns an object representing the Slate node for a heading. Example:
 *
 * ```js
 * heading("one", "My newsletter", { alignment: "center" })
 * ```
 *
 * @param {("one"|"two"|"three"|"four"|"five"|"six")} type
 * @param {string} text
 * @param {object} [data]
 * @returns {object}
 */
export function heading(type, text, data = {}) {
  return {
    object: "block",
    type: `heading-${type}`,
    data,
    nodes: [
      {
        object: "text",
        leaves: [{ object: "leaf", text, marks: [] }]
      }
    ]
  }
}

/**
 * Returns an object representing the Slate node for an image.
 *
 * @param {string} text
 * @param {object} [data]
 * @returns {object}
 */
export function img(data = {}) {
  return {
    object: "block",
    type: "image",
    data
  }
}

/**
 * Returns an object representing the Slate node for a paragraph.
 *
 * @param {string} text
 * @param {object} [data]
 * @param {object} [marks] Marks to apply to the text (bold, italic, etc.)
 * @returns {object}
 */
export function paragraph(text, data = {}, marks = []) {
  return {
    object: "block",
    type: "paragraph",
    data,
    nodes: [
      {
        object: "text",
        leaves: [
          {
            object: "leaf",
            text,
            marks: marks
          }
        ]
      }
    ]
  }
}

/**
 * Returns an object representing the Slate node for a button.
 *
 * @param {string} text
 * @param {object} [data]
 * @returns {object}
 */
export function button(text, data = {}) {
  return {
    object: "block",
    type: "button",
    data,
    nodes: [
      {
        object: "text",
        leaves: [{ object: "leaf", text, marks: [] }]
      }
    ]
  }
}

/**
 * Returns an object representing the Slate node for an unordered list with list-items.
 *
 * @param {array} items
 * @param {object} [data]
 * @returns {object}
 */
export function ul(items, data = {}) {
  const nodes = items.map(item => ({
    object: "block",
    type: "list-item",
    data: {},
    nodes: [
      {
        object: "block",
        type: "list-item-child",
        data: {},
        nodes: [
          {
            object: "text",
            leaves: [{ object: "leaf", text: item, marks: [] }]
          }
        ]
      }
    ]
  }))

  return {
    object: "block",
    type: "unordered-list",
    data,
    nodes
  }
}

/**
 * Returns an object representing the Slate node for a blockquote.
 *
 * @param {string} text
 * @param {object} [data]
 * @returns {object}
 */
export function blockquote(text, data = {}) {
  return {
    object: "block",
    type: "blockquote",
    data,
    nodes: [
      {
        object: "block",
        type: "blockquote-line",
        data: {},
        nodes: [
          {
            object: "text",
            leaves: [{ object: "leaf", text: text, marks: [] }]
          }
        ]
      }
    ]
  }
}

/**
 * Returns an object containing the basic frame for a Slate value.
 *
 * @param {string} text
 * @param {object} [data]
 * @returns {object}
 */
export function slate(nodes) {
  return {
    object: "value",
    document: {
      object: "document",
      data: {},
      nodes
    }
  }
}

/**
 * Outputs a blueprint for a text block with a set of custom nodes. Example:
 *
 * ```js
 * customTextBlock([
 *   heading("two", "My heading text"),
 *   paragraph("My paragraph text")
 * ])
 *
 * // {
 * //   type: "text",
 * //   properties: {
 * //     value: (a Slate value with the nodes you passed in)
 * //   }
 * // }
 * ```
 *
 * @param {Array} nodes
 * @returns {Object}
 */
export function customTextBlock(nodes) {
  return {
    type: "text",
    properties: {
      value: slate(nodes)
    }
  }
}
